/**
 * Cart drawer focus management.
 *
 * When an add-to-cart button is clicked and opens the cart drawer, focus
 * is returned to that button when the cart drawer is closed.
 *
 * Handles the cart drawer being dynamically re-rendered by watching the
 * drawer's parent element for the <cart-drawer> to be replaced, then
 * re-attaching the observer to the new instance.
 */
 
function initCartDrawerFocus() {
  let triggerButton = null;
  let drawerObserver = null;
 
  // --- Track the add-to-cart button that was clicked ---
 
  document.addEventListener('click', (e) => {
    const button = e.target.closest('form[action="/cart/add"] button[type="submit"]');
    if (!button) return;
 
    // Clean up any previous trigger marker
    const previousTrigger = document.querySelector('[data-cart-trigger]');
    if (previousTrigger) previousTrigger.removeAttribute('data-cart-trigger');
 
    triggerButton = button;
    triggerButton.setAttribute('data-cart-trigger', '');
  });
 
  // --- Observe the cart drawer for the "open" attribute being removed ---
 
  function observeDrawer(drawer) {
    if (drawerObserver) drawerObserver.disconnect();
 
    drawerObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'open' &&
          !drawer.hasAttribute('open') &&
          triggerButton
        ) {
          triggerButton.focus();
          triggerButton.removeAttribute('data-cart-trigger');
          triggerButton = null;
        }
      });
    });
 
    drawerObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ['open'],
    });
  }
 
  // --- Watch the drawer's parent for re-renders that replace <cart-drawer> ---
 
  function initParentObserver() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
 
    // Attach to the current drawer instance immediately
    observeDrawer(drawer);
 
    // Watch the parent so we catch when <cart-drawer> is replaced entirely
    const parent = drawer.parentElement;
    if (!parent) return;
 
    const parentObserver = new MutationObserver(() => {
      const newDrawer = parent.querySelector('cart-drawer');
      if (newDrawer && newDrawer !== drawer) {
        observeDrawer(newDrawer);
      }
    });
 
    parentObserver.observe(parent, { childList: true });
  }
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initParentObserver);
  } else {
    initParentObserver();
  }
}
 
initCartDrawerFocus();


/**
 * Slideshow live region announcer.
 *
 * For each <slideshow-carousel>, this script:
 *   1. Selects the existing .slideshow-live-region element inside <carousel-navigation>.
 *   2. Watches nav buttons for aria-current="true" via MutationObserver.
 *   3. When the active slide changes, reads the announcement text from the
 *      newly-active button's `data-slide-announcement` attribute and injects
 *      it into the live region so screen readers announce the change.
 *
 * Expected markup example:
 *
 *   <slideshow-carousel>
 *     ...slides...
 *     <carousel-navigation>
 *       <button aria-current="true"  data-slide-announcement="Slide 1 of 4: Our Summer Collection">...</button>
 *       <button aria-current="false" data-slide-announcement="Slide 2 of 4: New Arrivals">...</button>
 *       <button aria-current="false" data-slide-announcement="Slide 3 of 4: Sale Items">...</button>
 *       <button aria-current="false" data-slide-announcement="Slide 4 of 4: Featured Brands">...</button>
 *       <span class="slideshow-live-region" aria-live="polite" aria-atomic="true" aria-relevant="additions text"></span>
 *     </carousel-navigation>
 *   </slideshow-carousel>
 */
 
function initSlideshowLiveRegion() {
  const slideshows = document.querySelectorAll('slideshow-carousel');
 
  slideshows.forEach((slideshow) => {
    const nav = slideshow.querySelector('carousel-navigation');
    if (!nav) return;
 
    const buttons = nav.querySelectorAll('button');
    if (!buttons.length) return;
 
    // 1. Use the live region already present in the HTML.
    const liveRegion = nav.querySelector('.slideshow-live-region');
    if (!liveRegion) return;
 
    // 2. Watch each button for aria-current becoming "true".
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const button = mutation.target;
        if (button.getAttribute('aria-current') === 'true') {
          const message =
            button.getAttribute('data-slide-announcement') || '';
 
          // Briefly clear then set the text. Some screen readers won't
          // re-announce if the exact same string is injected twice in a row
          // (e.g. looping back to slide 1). The timeout forces a DOM change.
          liveRegion.textContent = '';
          requestAnimationFrame(() => {
            liveRegion.textContent = message;
          });
        }
      });
    });
 
    buttons.forEach((button) => {
      observer.observe(button, {
        attributes: true,
        attributeFilter: ['aria-current'],
      });
    });
  });
}
 
// Run after the DOM is ready.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSlideshowLiveRegion);
} else {
  initSlideshowLiveRegion();
}


/**
 * Video poster alt text.
 *
 * For each .shopify-section--video, finds all <video-media> elements with a
 * `data-poster-alt` attribute and applies that value as the alt text on the
 * poster <img> inside the <video> element.
 */
 
function initVideoPosterAlt() {
  document
    .querySelectorAll('.shopify-section--video video-media[data-poster-alt]')
    .forEach((videoMedia) => {
      const alt = videoMedia.getAttribute('data-poster-alt');
      const poster = videoMedia.querySelector('video img');
      if (poster) poster.setAttribute('alt', alt);
    });
}
 
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVideoPosterAlt);
} else {
  initVideoPosterAlt();
}


/**
 * Cart drawer live region announcer.
 *
 * When a quantity increase, decrease, or remove action is clicked inside
 * .cart-drawer__items, reads the announcement text from that element's
 * `data-cart-announcement` attribute and injects it into the
 * .cart-live-region element so screen readers announce the action.
 *
 * The .cart-live-region element lives outside the cart drawer so it
 * survives the cart drawer re-render that these actions trigger.
 *
 * Expected markup examples:
 *
 *   <a href="..." class="quantity-selector__button"
 *      data-cart-announcement="Quantity decreased. Now 1.">...</a>
 *
 *   <a href="..." class="quantity-selector__button"
 *      data-cart-announcement="Quantity increased. Now 3.">...</a>
 *
 *   <a href="..." data-cart-announcement="Product removed from cart.">Remove</a>
 *
 * Note: Because the cart re-renders after each action, the quantity values
 * in data-cart-announcement attributes will be freshly rendered each time,
 * so they'll always reflect the correct new quantity.
 */
 
function initCartLiveRegion() {
 
  // Use event delegation on the cart items container so we don't need to
  // re-bind listeners after each re-render of the cart drawer.
  document.addEventListener('click', (e) => {
    const actionEl = e.target.closest('[data-cart-announcement]');
    if (!actionEl) return;
 
    const message = actionEl.getAttribute('data-cart-announcement');
    if (!message) return;
 
    // Clear then set so repeated identical announcements (e.g. remove on a
    // qty-1 item) are still picked up by screen readers as a new change.
    
    setTimeout(() => {
      const liveRegion = document.querySelector('.cart-live-region');
      liveRegion.textContent = '';
      liveRegion.textContent = message;
    }, 2000);
  });
}
 
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartLiveRegion);
} else {
  initCartLiveRegion();
}


/**
 * Tracks visibility of <product-card> elements within <scroll-carousel> elements.
 * Adds "is-visible" class when a card is within the viewport, removes it when not.
 */
 
function initCarouselVisibility() {
  const carousels = document.querySelectorAll('featured-collections-carousel product-list');
 
  carousels.forEach((carousel) => {
    const cards = carousel.querySelectorAll('product-card');
    if (!cards.length) return;
 
    let userNavigated = false;
 
    // Listen for clicks on arrow buttons inside the carousel.
    // Adjust the selector below if your arrows use a different element/class.
    carousel.addEventListener('click', (e) => {
      console.log(e.target);
      if (e.target.closest('.prev-next-button')) {
        userNavigated = true;
        console.log('navigated')
      }
    });
 
    const observer = new IntersectionObserver(
      (entries) => {
        let firstNewlyVisible = null;
 
        entries.forEach((entry) => {
          const card = entry.target;
          card.classList.toggle('is-visible', entry.isIntersecting);
 
          if (entry.isIntersecting) {
            card.removeAttribute('tabindex');
            // Track the first newly-visible card (in DOM order)
            if (
              userNavigated &&
              (!firstNewlyVisible ||
                card.compareDocumentPosition(firstNewlyVisible) &
                  Node.DOCUMENT_POSITION_FOLLOWING)
            ) {
              firstNewlyVisible = card;
            }
          } else {
            card.setAttribute('tabindex', '-1');
          }
        });
 
        if (userNavigated && firstNewlyVisible) {
          // Ensure the card is focusable, then focus it
          if (!firstNewlyVisible.hasAttribute('tabindex')) {
            firstNewlyVisible.setAttribute('tabindex', '0');
          }
          firstNewlyVisible.focus({ preventScroll: true });
          userNavigated = false;
        }
      },
      {
        // Use the carousel itself as the scroll root so left/right overflow
        // cards are correctly treated as out-of-view even if the page
        // viewport would technically include them.
        root: carousel,
        threshold: 0, // any pixel visible = is-visible
      }
    );
 
    cards.forEach((card) => observer.observe(card));
  });
}
 
// Run after the DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCarouselVisibility);
} else {
  initCarouselVisibility();
}